import React, { useState, useEffect, useRef } from 'react';
import './NavBar.css';
import { FaUserCircle, FaBars, FaTimes, FaChevronDown, FaSearch } from 'react-icons/fa';
import logo from '../assets/logo.png';
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../context/SearchContext'; 

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  
  // Usar el contexto de búsqueda
  const { searchQuery, setSearchQuery } = useSearch();

  const dropdownRefs = useRef({});
  const navigate = useNavigate();

  const navigateToAccount = () => {
    navigate('/account');
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    handleLinkClick();
    
    if (user) {
      navigate('/page-store');
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown] &&
          !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        window.location.href = "/login-user";
      })
      .catch((error) => {
        console.error("Error al cerrar sesión:", error);
      });
  };

  // Manejar cambios en el buscador
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Limpiar búsqueda
  const clearSearch = () => {
    setSearchQuery('');
  };

  const isActive = (path) => window.location.pathname === path;

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`} aria-label="Main navigation">
      <div className="navbar-header">
        <div className="navbar-left">
          <a href="/page-store" className="navbar-logo-link" onClick={handleLogoClick}>
            <img src={logo} alt="BearMax Logo" className="navbar-logo-img" />
            <span className="navbar-logo-text"></span>
          </a>
        </div>

        <div className="navbar-right">
          {user && (
            <button onClick={handleLogout} className="navbar-logout desktop-only" title="Cerrar sesión">
              Cerrar sesión
            </button>
          )}
          
          <button
            className={`navbar-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>

      <div className={`navbar-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <ul className="navbar-links">
          <li>
            <a
              href="/page-store"
              className={isActive('/nosotros') ? 'active' : ''}
              onClick={handleLinkClick}
            >
              Tienda
            </a>
          </li>


          <li className="navbar-dropdown" ref={el => (dropdownRefs.current['servicios'] = el)}>
            <button
              className="navbar-dropdown-toggle"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'servicios'}
              onClick={() => toggleDropdown('servicios')}
            >
              Servicios
              <FaChevronDown className={`dropdown-arrow ${activeDropdown === 'servicios' ? 'rotated' : ''}`} />
            </button>
            <ul className={`navbar-dropdown-menu ${activeDropdown === 'servicios' ? 'show' : ''}`}>
              <li><a href="/tutorials" onClick={handleLinkClick}>📽️ Tutoriales</a></li>
              <li><a href="/code-login" onClick={handleLinkClick}>🤖 Bot de códigos</a></li>
              <li><a href="/seller-options" onClick={handleLinkClick}>⚙️ Herramientas de vendedor</a></li>
        
            </ul>
          </li>

          {/* Buscador mejorado */}
          <li className="navbar-search-item">
            <div className="navbar-search-container">
              <FaSearch className="navbar-search-icon" />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="navbar-search"
              />
            {searchQuery && (
              <button
                type="button"
                className="navbar-search-clear"
                onClick={clearSearch}
                aria-label="Limpiar búsqueda"
                title="Limpiar búsqueda"
              >
                <FaTimes size={14} />
              </button>
            )}
            </div>
          </li>

          <li className="navbar-account">
            <FaUserCircle size={22} aria-hidden="true" />
            <a
              href={user ? "/account" : "#"}
              aria-label="Mi Cuenta"
              onClick={(e) => {
                handleLinkClick();
                if (!user) {
                  e.preventDefault();
                  alert("Debes iniciar sesión o registrarte para acceder a tu cuenta.");
                } else {
                  navigateToAccount();
                }
              }}
            >
              Mi Cuenta
            </a>
          </li>

          {user && (
            <li className="mobile-logout-container">
              <button onClick={handleLogout} className="navbar-logout mobile-only" title="Cerrar sesión">
                Cerrar sesión
              </button>
            </li>
          )}
        </ul>
      </div>

      {isMobileMenuOpen && <div className="mobile-overlay" onClick={toggleMobileMenu}></div>}
    </nav>
  );
};

export default Navbar;